// ═══════════════════════════════════════════════════════════════
// QuotaAdminModal.tsx – kvóták (superadmin): globális alapérték és a tárhelybővítési igények
//
// A globális alapértéket minden szervezet követi, ahol nincs egyedi érték (a szervezet szerkesztőjében
// „Globális alapérték” jelölővel). 0 = korlátlan. Az igények a szervezetek admináitól érkeznek
// (figyelmeztető sáv → „Plusz tárhely igénylése”); jóváhagyáskor a szervezet egyedi korlátot kap.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { ApiClient, type QuotaDefaults, type QuotaRequestRow } from '../../api/client';
import { X, HardDrive, Save, RefreshCw, Check, Ban, AlertTriangle } from 'lucide-react';

type Req = QuotaRequestRow;

export const QuotaAdminModal: React.FC<{ isOpen: boolean; onClose: () => void; onChanged?: () => void }> = ({ isOpen, onClose, onChanged }) => {
  const [defaults, setDefaults] = useState<QuotaDefaults | null>(null);
  const [draft, setDraft] = useState({ max_storage_mb: '', max_records: '', warn_percent: '' });
  const [requests, setRequests] = useState<Req[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [approveMb, setApproveMb] = useState<Record<number, string>>({});
  const [rejectReason, setRejectReason] = useState<Record<number, string>>({});

  const load = async () => {
    setError(null);
    const r = await ApiClient.getAdminQuota();
    if (r.ok && r.data) {
      setDefaults(r.data.defaults);
      setDraft({ max_storage_mb: String(r.data.defaults.max_storage_mb), max_records: String(r.data.defaults.max_records), warn_percent: String(r.data.defaults.warn_percent) });
      setRequests(r.data.requests);
    } else setError(r.error || 'A kvóták nem tölthetők be');
  };
  useEffect(() => { if (isOpen) { setNotice(null); load(); } }, [isOpen]);
  if (!isOpen) return null;

  const saveDefaults = async () => {
    setBusy('defaults'); setError(null); setNotice(null);
    const r = await ApiClient.setQuotaDefaults({ max_storage_mb: Number(draft.max_storage_mb), max_records: Number(draft.max_records), warn_percent: Number(draft.warn_percent) });
    setBusy(null);
    if (r.ok && r.data) { setDefaults(r.data); setNotice('A globális alapérték mentve – minden szervezetnél érvényes, ahol nincs egyedi érték.'); onChanged?.(); } else setError(r.error || 'Nem sikerült menteni');
  };
  const decide = async (q: Req, approve: boolean) => {
    const proposed = (q.limit_mb_at_request || defaults?.max_storage_mb || 0) + q.additional_mb;
    const val = approveMb[q.tenant_id] ?? String(proposed);
    setBusy(`req${q.tenant_id}`); setError(null); setNotice(null);
    const r = await ApiClient.decideQuotaRequest(q.tenant_id, approve, approve ? { max_storage_mb: Number(val) } : { reason: rejectReason[q.tenant_id] || '' });
    setBusy(null);
    if (r.ok) { setNotice(approve ? `${q.tenant_name}: új tárhelykorlát ${val} MB.` : `${q.tenant_name}: az igény elutasítva.`); load(); onChanged?.(); } else setError(r.error || 'Nem sikerült');
  };
  const pending = requests.filter((q) => q.status === 'pending');
  const decided = requests.filter((q) => q.status !== 'pending').slice(0, 10);
  const field = (k: keyof typeof draft, label: string, hint: string) => (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">{label}</span>
      <input type="number" min={0} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
        className="w-full px-3 py-1.5 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white" />
      <span className="text-[10px] text-slate-500">{hint}</span>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs" role="dialog" aria-modal="true" aria-labelledby="quota-admin-title"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div>
            <h2 id="quota-admin-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><HardDrive className="w-5 h-5 text-indigo-600" /> Kvóták</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">A foglalt tárhelybe a közzétett mellékletek, a Lomtár és az Archívum is beleszámít. A küszöb felett figyelmeztetés jelenik meg, 100%-nál új feltöltés / bejegyzés nem lehetséges (az archiválás igen).</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {error && <p role="alert" className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{error}</p>}
          {notice && <p role="status" className="text-xs text-emerald-700 dark:text-emerald-400">{notice}</p>}

          <section className="p-4 rounded-xl border border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/50 dark:bg-indigo-950/20 space-y-3">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Globális alapérték</h3>
            <p className="text-[11px] text-slate-600 dark:text-slate-400">Minden szervezet ezt kapja, kivéve ahol a szervezet szerkesztőjében egyedi érték van beállítva.</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {field('max_storage_mb', 'Tárhely (MB)', '0 = korlátlan')}
              {field('max_records', 'Bejegyzések (db)', '0 = korlátlan')}
              {field('warn_percent', 'Figyelmeztetés (%)', '50–99, alapból 80')}
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={saveDefaults} disabled={busy !== null || !defaults}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer disabled:opacity-50">
                {busy === 'defaults' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Mentés
              </button>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Tárhelybővítési igények {pending.length > 0 && <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300">{pending.length} vár</span>}</h3>
            {!pending.length && <p className="text-xs text-slate-500">Nincs elbírálásra váró igény.</p>}
            {pending.map((q) => {
              const proposed = (q.limit_mb_at_request || defaults?.max_storage_mb || 0) + q.additional_mb;
              return (
                <div key={q.tenant_id} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                  <div className="text-sm font-semibold text-slate-900 dark:text-white">{q.tenant_name} <span className="text-xs font-normal text-slate-500">· +{q.additional_mb} MB · {new Date(q.created_at).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' })} · {q.user_name}{q.ticket_number ? ` · ${q.ticket_number}` : ''}</span></div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    Az igényléskor: {(q.used_bytes_at_request / 1048576).toFixed(1)} MB foglalt / {q.limit_mb_at_request ? `${q.limit_mb_at_request} MB` : 'korlátlan'}{q.own_max_storage_mb === null ? ' (globális alapérték)' : ''}.
                    {q.message ? <> Indoklás: „{q.message}”</> : null}
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-xs">
                      <span className="block font-semibold text-slate-700 dark:text-slate-300 mb-0.5">Új korlát (MB)</span>
                      <input type="number" min={0} value={approveMb[q.tenant_id] ?? String(proposed)} onChange={(e) => setApproveMb({ ...approveMb, [q.tenant_id]: e.target.value })}
                        className="w-28 px-2.5 py-1.5 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white" />
                    </label>
                    <button type="button" onClick={() => decide(q, true)} disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer disabled:opacity-50">
                      {busy === `req${q.tenant_id}` ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Jóváhagyás
                    </button>
                    <input type="text" placeholder="Elutasítás oka (nem kötelező)" value={rejectReason[q.tenant_id] || ''} onChange={(e) => setRejectReason({ ...rejectReason, [q.tenant_id]: e.target.value })}
                      className="flex-1 min-w-[10rem] px-2.5 py-1.5 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-white" aria-label="Elutasítás oka" />
                    <button type="button" onClick={() => decide(q, false)} disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 cursor-pointer disabled:opacity-50">
                      <Ban className="w-3.5 h-3.5" /> Elutasítás
                    </button>
                  </div>
                </div>
              );
            })}
            {decided.length > 0 && (
              <details className="text-xs text-slate-600 dark:text-slate-400">
                <summary className="cursor-pointer font-semibold">Korábbi döntések ({decided.length})</summary>
                <ul className="mt-2 space-y-1">
                  {decided.map((q) => (
                    <li key={q.tenant_id}>{q.tenant_name}: +{q.additional_mb} MB – {q.status === 'approved' ? `jóváhagyva (${q.new_limit_mb} MB)` : `elutasítva${q.reason ? ` (${q.reason})` : ''}`}{q.decided_at ? `, ${new Date(q.decided_at).toLocaleDateString('hu-HU')}` : ''}{q.decided_by ? ` · ${q.decided_by}` : ''}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default QuotaAdminModal;
